import React, { useState, useRef, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CaretDown, Check, MagnifyingGlass, X } from "@phosphor-icons/react";
import { cn } from "./cn";

export default function MultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  label,
  icon: Icon,
  searchable = true,
  maxChips = 3,
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selectedOpts = options.filter((o) => value.includes(o.value));
  const filtered = useMemo(
    () => (searchable && q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options),
    [options, q, searchable]
  );

  const toggle = (v) =>
    onChange?.(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  const remove = (e, v) => {
    e.stopPropagation();
    onChange?.(value.filter((x) => x !== v));
  };

  return (
    <div className={cn("w-full", className)} ref={ref}>
      {label && <label className="mb-1.5 block text-start text-sm font-medium text-ink/90">{label}</label>}
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "flex min-h-[2.75rem] w-full items-center justify-between gap-2 rounded-xl border border-line bg-surface-2 px-2.5 py-1.5 text-sm outline-none transition",
            "focus:border-accent focus:ring-2 focus:ring-accent/30",
            open && "border-accent ring-2 ring-accent/30",
            disabled && "opacity-60"
          )}
        >
          <span className="flex flex-1 flex-wrap items-center gap-1.5">
            {Icon && <Icon size={17} className="ml-1 shrink-0 text-faint" />}
            {selectedOpts.length === 0 && <span className="px-1 text-faint">{placeholder}</span>}
            {selectedOpts.slice(0, maxChips).map((o) => (
              <span
                key={o.value}
                className="inline-flex items-center gap-1 rounded-lg bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent-strong"
              >
                {o.label}
                <X size={12} weight="bold" className="hover:opacity-70" onClick={(e) => remove(e, o.value)} />
              </span>
            ))}
            {selectedOpts.length > maxChips && (
              <span className="rounded-lg bg-surface px-2 py-0.5 text-xs text-muted">
                +{selectedOpts.length - maxChips}
              </span>
            )}
          </span>
          <CaretDown size={16} className={cn("shrink-0 text-faint transition-transform", open && "rotate-180")} />
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-line bg-surface shadow-pop"
            >
              {searchable && (
                <div className="flex items-center gap-2 border-b border-line px-3 py-2">
                  <MagnifyingGlass size={16} className="text-faint" />
                  <input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search…"
                    className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-faint"
                  />
                </div>
              )}
              <ul className="max-h-60 overflow-auto p-1">
                {filtered.length === 0 && <li className="px-3 py-2 text-sm text-faint">No results</li>}
                {filtered.map((o) => {
                  const active = value.includes(o.value);
                  return (
                    <li key={o.value}>
                      <button
                        type="button"
                        onClick={() => toggle(o.value)}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-sm transition",
                          active ? "text-ink" : "text-ink hover:bg-surface-2"
                        )}
                      >
                        <span
                          className={cn(
                            "grid h-4 w-4 place-items-center rounded border transition",
                            active ? "border-accent bg-accent text-accent-fg" : "border-line"
                          )}
                        >
                          {active && <Check size={12} weight="bold" />}
                        </span>
                        <span className="truncate">{o.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
